import Decimal from 'decimal.js';
import { convertUnitPrice, isKgUnit, kgOption, type UomOption } from './uom';

/**
 * Pricing Engine (ADR-016, docs/phases/P06.md §5) – hàm thuần, dùng chung cho API (nguồn sự thật) và giao diện (xem trước).
 *
 * Giá chuẩn của 1 mã cho 1 khách, tính trên 1 ĐVT chính, theo thứ tự ưu tiên (DEC-097, DEC-134):
 *   1. giá riêng của khách (gồm giá đã chốt, DEC-138)
 *   2. giá cố định của nhóm khách
 *   3. giá chung × (1 + % nhóm)        (khách không có nhóm: giá chung)
 *   4. quy tắc giá theo hãng × (1 + % nhóm): (giá nhà máy + Σ dải + lãi) đ/kg × kg của 1 ĐVT chính
 *   5. products.sale_price (giá tham khảo Asia, DEC-060 A)
 *   6. giá tham khảo mạng đã lưu của mã (DEC-141 A: sau ≥ 3 lần tìm trên mạng, còn hạn 30 ngày) – giá Asia/ERP luôn đứng trước
 *   7. không có → NV nhập tay (priceSource = NONE)
 *
 * Giá theo kg (Design System §16–18): giá đặt trên 1 kg, giá 1 ĐVT chính = giá/kg × kg của 1 ĐVT chính.
 * Thiếu số kg/ĐVT thì bỏ qua nguồn đó (cảnh báo MISSING_KG_FACTOR) và xét nguồn kế tiếp – không đoán giá.
 *
 * Làm tròn ROUND_HALF_UP về đồng (VND không số lẻ): giá/kg (hoặc giá/ĐVT) sau % nhóm làm tròn trước,
 * rồi giá 1 ĐVT chính = round(giá/kg × kg). Đầu vào làm tròn theo cột DB (giá 2 số lẻ, kg 6 số lẻ, % 3 số lẻ).
 */
const D = Decimal.clone({ precision: 40, rounding: Decimal.ROUND_HALF_UP });

export type PriceBasis = 'kg' | 'base_uom';
export type PriceSource = 'CUSTOMER' | 'GROUP_FIXED' | 'GROUP_PERCENT' | 'BASE' | 'BRAND_RULE' | 'PRODUCT' | 'SUPPLIER' | 'WEB' | 'NONE';

export const PRICE_SOURCE_LABELS: Record<PriceSource, string> = {
  CUSTOMER: 'Giá riêng của khách',
  GROUP_FIXED: 'Giá nhóm khách',
  GROUP_PERCENT: 'Giá chung ± % nhóm',
  BASE: 'Giá chung',
  BRAND_RULE: 'Giá theo hãng',
  PRODUCT: 'Giá tham khảo Asia',
  SUPPLIER: 'Giá theo bảng giá nhà cung cấp',
  WEB: 'Giá tham khảo mạng',
  NONE: 'Chưa có giá – nhập tay',
};

type Num = number | string;

export interface PriceEntry {
  price: Num;
  basis: PriceBasis;
}

export interface PriceBand {
  label?: string | null;
  thicknessMinMm?: Num | null;
  thicknessMaxMm?: Num | null;
  odMinMm?: Num | null;
  odMaxMm?: Num | null;
  addPerKg: Num;
}

export interface BrandRuleInput {
  name: string;
  factoryPricePerKg: Num;
  marginPerKg: Num;
  bands: PriceBand[];
}

export interface PricingInput {
  product: {
    /** Số kg của 1 ĐVT chính (hệ số Kg Asia, weight_kg, hoặc 1 nếu ĐVT chính là kg). */
    kgPerBaseUom?: Num | null;
    thicknessMm?: Num | null;
    odMm?: Num | null;
    salePrice?: Num | null;
  };
  customerPrice?: PriceEntry | null;
  /** Nhóm khách của khách (bảng giá nhóm). null = khách không có nhóm. */
  group?: { name: string; adjustPercent: Num; fixedPrice?: PriceEntry | null } | null;
  basePrice?: PriceEntry | null;
  brandRule?: BrandRuleInput | null;
  /**
   * CR-009: giá NCC rẻ nhất còn hiệu lực (chưa VAT) + lãi đề xuất; chỉ dùng khi không có giá ERP/Asia. basis kg = giá/kg (mã có quy đổi Kg).
   * Giá NCC là giá nhập: chỉ nằm trong breakdown (ẩn với người không có price.view_cost).
   */
  supplierPrice?: { price: Num; basis: PriceBasis; markupPercent: Num; supplier: string } | null;
  /** Giá tham khảo mạng đã lưu (DEC-141 A), chưa VAT, trên 1 ĐVT chính; chỉ dùng khi không có nguồn nào khác. */
  webPrice?: { price: Num; note?: string | null } | null;
}

export interface PricingWarning {
  code: 'MISSING_KG_FACTOR';
  message: string;
}

/** Chi tiết cách ra giá. Các trường giá vốn (giá nhà máy, dải, lãi) chỉ trả cho người có price.view_cost. */
export interface PricingBreakdown {
  sourcePrice: number;
  groupAdjustPercent: number | null;
  factoryPricePerKg?: number;
  bandAddPerKg?: number;
  bands?: { label: string | null; addPerKg: number }[];
  marginPerKg?: number;
  ruleName?: string;
  /** CR-009: giá NCC (chưa VAT, theo basis), lãi % và tên NCC – chỉ price.view_cost */
  supplierCost?: number;
  markupPercent?: number;
  supplierName?: string;
}

export interface PricingResult {
  /** Giá chuẩn trên 1 ĐVT chính (VND). null khi chưa có giá. */
  listPrice: number | null;
  /** Giá/kg khi giá đặt theo kg (sau % nhóm). */
  pricePerKg: number | null;
  priceBasis: PriceBasis | null;
  priceSource: PriceSource;
  appliedRules: string[];
  warnings: PricingWarning[];
  breakdown: PricingBreakdown | null;
}

const isSet = (v: Num | null | undefined): v is Num => v !== null && v !== undefined && v !== '';
const dp = (v: Num, places: number) => new D(v).toDecimalPlaces(places);
const vnd = (v: Decimal) => v.toDecimalPlaces(0).toNumber();
const fmt = (v: number) => v.toLocaleString('vi-VN');

/** Dải khớp khi mọi giới hạn có đặt đều thỏa (khoảng đóng). Dải đòi độ dày/OD mà hàng không có thì không khớp. */
export function bandMatches(band: PriceBand, thicknessMm: Num | null | undefined, odMm: Num | null | undefined): boolean {
  const within = (v: Num | null | undefined, min: Num | null | undefined, max: Num | null | undefined) => {
    if (!isSet(min) && !isSet(max)) return true;
    if (!isSet(v)) return false;
    const x = new D(v);
    return (!isSet(min) || x.gte(min)) && (!isSet(max) || x.lte(max));
  };
  return within(thicknessMm, band.thicknessMinMm, band.thicknessMaxMm) && within(odMm, band.odMinMm, band.odMaxMm);
}

/** Giá bán/kg theo quy tắc hãng: giá nhà máy + Σ dải khớp + lãi (DEC-134). */
export function brandRulePricePerKg(rule: BrandRuleInput, thicknessMm?: Num | null, odMm?: Num | null) {
  const matched = rule.bands.filter((b) => bandMatches(b, thicknessMm, odMm));
  const bandAdd = matched.reduce((s, b) => s.add(dp(b.addPerKg, 2)), new D(0));
  const perKg = dp(rule.factoryPricePerKg, 2).add(bandAdd).add(dp(rule.marginPerKg, 2));
  return { perKg, bandAdd, matched };
}

export function priceLine(input: PricingInput): PricingResult {
  const warnings: PricingWarning[] = [];
  const kg = isSet(input.product.kgPerBaseUom) && new D(input.product.kgPerBaseUom).gt(0) ? dp(input.product.kgPerBaseUom, 6) : null;
  const group = input.group ?? null;
  const pct = group ? dp(group.adjustPercent, 3) : null;
  let warnedKg = false;
  const noKg = (what: string) => {
    if (!warnedKg) warnings.push({ code: 'MISSING_KG_FACTOR', message: `${what} đặt theo kg nhưng hàng chưa có quy đổi Kg, bỏ qua` });
    warnedKg = true;
  };

  /** Quy giá đặt theo cơ sở (kg / ĐVT chính) sau % nhóm ra giá 1 ĐVT chính. */
  const finish = (
    entry: { price: Decimal; basis: PriceBasis },
    source: PriceSource,
    applyPct: boolean,
    rules: string[],
    extra: Partial<PricingBreakdown> = {},
  ): PricingResult | null => {
    let unit = entry.price;
    if (applyPct && pct && !pct.isZero()) {
      unit = unit.mul(new D(100).add(pct)).div(100);
      rules.push(`${group!.name}: ${pct.gt(0) ? '+' : ''}${pct.toNumber()}%`);
    }
    const unitVnd = new D(vnd(unit));
    if (entry.basis === 'kg') {
      if (!kg) return null;
      const list = vnd(unitVnd.mul(kg));
      rules.push(`${fmt(unitVnd.toNumber())} đ/kg × ${kg.toNumber()} kg = ${fmt(list)} đ`);
      return {
        listPrice: list,
        pricePerKg: unitVnd.toNumber(),
        priceBasis: 'kg',
        priceSource: source,
        appliedRules: rules,
        warnings,
        breakdown: { sourcePrice: entry.price.toNumber(), groupAdjustPercent: applyPct && pct ? pct.toNumber() : null, ...extra },
      };
    }
    return {
      listPrice: unitVnd.toNumber(),
      pricePerKg: kg ? vnd(unitVnd.div(kg)) : null,
      priceBasis: 'base_uom',
      priceSource: source,
      appliedRules: rules,
      warnings,
      breakdown: { sourcePrice: entry.price.toNumber(), groupAdjustPercent: applyPct && pct ? pct.toNumber() : null, ...extra },
    };
  };
  const entry = (e: PriceEntry) => ({ price: dp(e.price, 2), basis: e.basis });

  const tries: (() => PricingResult | null)[] = [
    () => {
      if (!input.customerPrice) return null;
      const r = finish(entry(input.customerPrice), 'CUSTOMER', false, [PRICE_SOURCE_LABELS.CUSTOMER]);
      if (!r) noKg('Giá riêng của khách');
      return r;
    },
    () => {
      if (!group?.fixedPrice) return null;
      const r = finish(entry(group.fixedPrice), 'GROUP_FIXED', false, [`Giá cố định nhóm ${group.name}`]);
      if (!r) noKg(`Giá nhóm ${group.name}`);
      return r;
    },
    () => {
      if (!input.basePrice) return null;
      const withPct = !!pct && !pct.isZero();
      const r = finish(entry(input.basePrice), withPct ? 'GROUP_PERCENT' : 'BASE', true, [PRICE_SOURCE_LABELS.BASE]);
      if (!r) noKg('Giá chung');
      return r;
    },
    () => {
      const rule = input.brandRule;
      if (!rule) return null;
      if (!kg) {
        noKg(`Giá theo hãng (${rule.name})`);
        return null;
      }
      const { perKg, bandAdd, matched } = brandRulePricePerKg(rule, input.product.thicknessMm, input.product.odMm);
      return finish({ price: perKg, basis: 'kg' }, 'BRAND_RULE', true, [`Giá theo hãng: ${rule.name}`], {
        ruleName: rule.name,
        factoryPricePerKg: dp(rule.factoryPricePerKg, 2).toNumber(),
        bandAddPerKg: bandAdd.toNumber(),
        bands: matched.map((b) => ({ label: b.label ?? null, addPerKg: dp(b.addPerKg, 2).toNumber() })),
        marginPerKg: dp(rule.marginPerKg, 2).toNumber(),
      });
    },
    () => {
      const sp = input.product.salePrice;
      if (!isSet(sp) || !new D(sp).gt(0)) return null;
      return finish({ price: dp(sp, 2), basis: 'base_uom' }, 'PRODUCT', false, [PRICE_SOURCE_LABELS.PRODUCT]);
    },
    () => {
      const sp = input.supplierPrice;
      if (!sp || !isSet(sp.price) || !new D(sp.price).gt(0)) return null;
      const cost = dp(sp.price, 2);
      const markup = dp(sp.markupPercent, 3);
      const r = finish({ price: cost.mul(new D(100).add(markup)).div(100), basis: sp.basis }, 'SUPPLIER', false, [PRICE_SOURCE_LABELS.SUPPLIER], {
        supplierCost: cost.toNumber(),
        markupPercent: markup.toNumber(),
        supplierName: sp.supplier,
      });
      if (!r) noKg('Giá nhà cung cấp');
      return r;
    },
    () => {
      const w = input.webPrice;
      if (!w || !isSet(w.price) || !new D(w.price).gt(0)) return null;
      return finish({ price: dp(w.price, 2), basis: 'base_uom' }, 'WEB', false, [w.note ? `${PRICE_SOURCE_LABELS.WEB}: ${w.note}` : PRICE_SOURCE_LABELS.WEB]);
    },
  ];

  for (const t of tries) {
    const r = t();
    if (r) return r;
  }
  return { listPrice: null, pricePerKg: null, priceBasis: null, priceSource: 'NONE', appliedRules: [], warnings, breakdown: null };
}

/** Bỏ các trường giá vốn (giá nhà máy, dải, lãi) khi người xem không có price.view_cost (DEC-134, CLAUDE.md §4). */
export function hidePricingCost(r: PricingResult): PricingResult {
  if (!r.breakdown) return r;
  const { factoryPricePerKg: _f, bandAddPerKg: _b, bands: _bs, marginPerKg: _m, supplierCost: _c, markupPercent: _p, supplierName: _s, ...rest } = r.breakdown;
  // Giá NCC + lãi: sourcePrice (giá sau lãi, chưa làm tròn) thay bằng giá bán đã làm tròn
  return { ...r, breakdown: r.priceSource === 'SUPPLIER' ? { ...rest, sourcePrice: (r.priceBasis === 'kg' ? r.pricePerKg : r.listPrice) ?? 0 } : rest };
}

/**
 * P06 B5 (DEC-119, P06.md §8): người không có `price_list.read` (CTV) không thấy giá chuẩn, nguồn giá, quy tắc, cảnh báo giá.
 * Chỉ còn khung kết quả rỗng: CTV tự nhập giá đề xuất.
 */
export function hideListPrice(_r: PricingResult): PricingResult {
  return { listPrice: null, pricePerKg: null, priceBasis: null, priceSource: 'NONE', appliedRules: [], warnings: [], breakdown: null };
}

// ---------------------------------------------------------------------------
// P06 B4: áp giá vào dòng báo giá (docs/phases/P06.md §5 mục 2, 4, 6; DEC-005, DEC-100, Design System §16–18)
// ---------------------------------------------------------------------------

/** DEC-005 A: NV chỉ chọn 0/5/8/10%; đổi khác mặc định của sản phẩm (Asia) phải ghi lý do. */
export const VAT_OPTIONS = [0, 5, 8, 10] as const;

/** VAT hợp lệ: thuộc 0/5/8/10, hoặc đúng mặc định của sản phẩm (mã Asia có thuế suất khác vẫn giữ được). */
export function vatAllowed(vatRate: number, defaultVatRate?: number | null): boolean {
  return (VAT_OPTIONS as readonly number[]).includes(Number(vatRate)) || (defaultVatRate != null && Number(vatRate) === Number(defaultVatRate));
}

/** Đổi VAT khác mặc định của sản phẩm → bắt buộc lý do (DEC-005 A). Dòng tự nhập không có mặc định. */
export function vatNeedsReason(vatRate: number, defaultVatRate?: number | null): boolean {
  return defaultVatRate != null && Number(vatRate) !== Number(defaultVatRate);
}

/**
 * DEC-100 A: CK hiệu dụng (%) = (1 − đơn giá × (1 − CK%) ÷ giá chuẩn) × 100, 2 số lẻ.
 * Giá chuẩn và đơn giá cùng 1 ĐVT bán. NV sửa đơn giá thấp hơn bảng giá cũng là chiết khấu; bán cao hơn ra số âm.
 * Không có giá chuẩn → null (không đánh giá được).
 */
export function effectiveDiscountPercent(unitPrice: Num, discountPercent: Num | null | undefined, listPrice: Num | null | undefined): number | null {
  if (!isSet(listPrice) || !new D(listPrice).gt(0)) return null;
  const net = dp(unitPrice, 2).mul(new D(100).minus(dp(isSet(discountPercent) ? discountPercent : 0, 2))).div(100);
  return new D(1).minus(net.div(dp(listPrice, 2))).mul(100).toDecimalPlaces(2).toNumber();
}

/** Số kg của 1 ĐVT chính: ĐVT chính là kg → 1; có ĐVT phụ Kg (hệ số Asia) → hệ số; không thì weight_kg; không có → null. */
export function kgPerBase(options: UomOption[], weightKg?: Num | null): number | null {
  const kg = kgOption(options);
  if (kg) return kg.factor;
  return isSet(weightKg) && new D(weightKg).gt(0) ? dp(weightKg, 6).toNumber() : null;
}

export interface SaleUnit {
  /** ĐVT bán của dòng */
  uom: string;
  /** 1 ĐVT chính = factor × ĐVT bán */
  factor: number;
}

/**
 * Giá chuẩn theo ĐVT bán của dòng. Giá chuẩn của engine tính trên 1 ĐVT chính (listPrice);
 * bán theo Kg mà giá đặt theo kg thì giá chuẩn = đúng giá/kg (không quy vòng qua ĐVT chính để khỏi lệch lẻ),
 * ĐVT khác quy theo hệ số (convertUnitPrice).
 */
export function saleUnitListPrice(r: Pick<PricingResult, 'listPrice' | 'pricePerKg' | 'priceBasis'>, unit: SaleUnit): number | null {
  if (r.listPrice === null) return null;
  if (r.priceBasis === 'kg' && r.pricePerKg !== null && isKgUnit(unit.uom)) return r.pricePerKg;
  return unit.factor === 1 ? r.listPrice : convertUnitPrice(r.listPrice, 1, unit.factor);
}

/**
 * Design System §18: NV sửa giá/kg, đơn giá ĐVT bán tự tính. Giá 1 ĐVT chính = round(giá/kg × kg/ĐVT chính) (giống engine),
 * rồi quy sang ĐVT bán; bán theo Kg thì đơn giá = giá/kg.
 * VD 25.000 đ/kg × 42,66 kg/cây = 1.066.500 đ/cây (§11 ca 2c).
 */
export function unitPriceFromKg(pricePerKg: Num, kgPerBaseUom: Num, unit: SaleUnit): number {
  const perKg = dp(pricePerKg, 2);
  if (isKgUnit(unit.uom)) return perKg.toNumber();
  const base = vnd(perKg.mul(dp(kgPerBaseUom, 6)));
  return unit.factor === 1 ? base : convertUnitPrice(base, 1, unit.factor);
}
